import React, { useState, useEffect, useRef } from 'react';
import { API_URLS } from '../config'; // Import API_URLS
import './styles/AppearanceSettings.css'; // Import CSS for styling

const AppearanceSettings = ({ businessId, setAccentColor, accentColor: initialAccentColorProp, onSettingsChange }) => {
  // State for the color picker input and "New" color preview
  const [selectedAccentColor, setSelectedAccentColor] = useState(initialAccentColorProp || '#F4A460');

  // State for the "Current" color preview box. This should reflect the color at load/last save.
  const [currentDisplayColor, setCurrentDisplayColor] = useState(initialAccentColorProp || '#F4A460');

  // State copy of the current initial color
  const [initialAccentColor, setInitialAccentColor] = useState(initialAccentColorProp || '#F4A460');

  const [error, setError] = useState(''); // Add error state
  const [successMessage, setSuccessMessage] = useState(''); // Add success state
  
  // Reference to track if component has been mounted
  const initialMountRef = useRef(true);

  // Effect to synchronize states when initialAccentColorProp or businessId changes.
  // This ensures that if the parent component provides a new baseline color (e.g., after an external update or loading a new business),
  // both the color picker ('selectedAccentColor') and the "Current" display ('currentDisplayColor') are reset.
  useEffect(() => {
    // Always update the selected color (preview)
    setSelectedAccentColor(initialAccentColorProp || '#F4A460');
    
    // Only update current display color on initial mount or when businessId changes
    if (initialMountRef.current || businessId) {
      setCurrentDisplayColor(initialAccentColorProp || '#007BFF');
      initialMountRef.current = false;
    }
  }, [initialAccentColorProp, businessId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    const token = localStorage.getItem('token');
    if (!token || !businessId) {
        setError("Authentication token or Business ID is missing.");
        return;
    }

    try {
        const response = await fetch(API_URLS.appearanceSettings(businessId), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            accentColor: selectedAccentColor, // Send the color chosen by the user
            // headerImageLink, logoImageLink, businessName, etc.
          }),
        });

        const data = await response.json();

        if (response.ok) {
          console.log('Appearance settings updated successfully');
          setSuccessMessage('Appearance settings updated successfully!');
          // After a successful save, the "Current" color becomes the color that was just saved.
          setCurrentDisplayColor(selectedAccentColor);
          // The parent component is expected to update initialAccentColorProp with selectedAccentColor.
        } else {
          console.error('Failed to update appearance settings:', data.message);
          setError(data.message || 'Failed to update appearance settings');
        }
    } catch(err) {
        console.error('Error updating appearance settings:', err);
        setError('An error occurred while updating appearance settings.');
    }
  };

  const handleAccentColorChange = (e) => {
    const newColor = e.target.value;
    setSelectedAccentColor(newColor); // Only update the "New" preview color

    // If setAccentColor prop is provided (e.g., for live preview in parent), call it.
    if (setAccentColor) {
      setAccentColor(newColor);
    }
    // If onSettingsChange prop is provided (e.g., for other reactions in parent to color change), call it.
    if (onSettingsChange) {
      onSettingsChange(newColor); // Pass the newly selected color
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <h3>Appearance Settings</h3>
      {error && <p className="error-message">{error}</p>}
      {successMessage && <p className="success-message">{successMessage}</p>}
      <label htmlFor="accentColorInput">Accent Color:</label>
      <div className="color-picker-section">
        <div className="color-preview-item">
          <p className="color-preview-label">Old:</p>
          <div
            className="color-preview-box"
            style={{ backgroundColor: initialAccentColor }}
          ></div>
        </div>
        <div className="color-preview-item">
          <p className="color-preview-label">New:</p>
          <div
            className="color-preview-box"
            style={{ backgroundColor: selectedAccentColor }}
          ></div>
        </div>
        <input
          id="accentColorInput"
          type="color"
          value={selectedAccentColor} // Use local state for input value
          onChange={handleAccentColorChange}
          className="color-input-element"
        />
      </div>
      <button type="submit">Save Changes</button>
    </form>
  );
};

export default AppearanceSettings;